// Phase 4 — reusable position re-anchor tween. Glides a Pixi Container's
// (x, y) from one logical position to another on the shared tweedle Group.
// General: any old->new move (board-grow re-anchor now; opponent-move replay
// in Phase 5). Caller snapshots endpoints (stale-target safety) and drains
// the returned tween on destroy (Pitfall 5 lock).

import type { Group } from 'tweedle.js'
import { Tween } from 'tweedle.js'
import { type CubicBezier, cubicBezierEasing, EASING } from './tween-config'

export interface PositionTarget {
  x: number
  y: number
}

export interface PositionAnchorTweenConfig {
  readonly target: PositionTarget
  readonly fromPos: PositionTarget
  readonly toPos: PositionTarget
  /** ms — pass selectDuration('dur-board-grow', reducedMotion). 0 = snap. */
  readonly duration: number
  readonly group: Group
  readonly onComplete?: () => void
  /**
   * Optional cubic-bezier easing for the glide. Defaults to the canonical
   * `ease-snap-out` curve so existing callers (opponent-move replay, etc.) are
   * byte-for-byte unchanged. The Phase-2 "doors fling open" reveal reanchor
   * passes the bouncy curve (control-y > 1) for a mild parting overshoot.
   * Honored ONLY when duration > 0 — the duration<=0 snap path ignores it
   * entirely so reduced motion stays an instant teleport.
   */
  readonly easing?: CubicBezier
}

const SNAP_OUT_VALUE =
  EASING['ease-snap-out'].type === 'cubic-bezier'
    ? EASING['ease-snap-out'].value
    : ([0, 0, 0.2, 1] as const)

/**
 * Create + start a position re-anchor tween, or snap instantly when
 * duration <= 0 (reduced motion). Returns the live Tween (for destroy-drain)
 * or null when it snapped.
 */
export function createPositionAnchorTween(
  config: PositionAnchorTweenConfig,
): Tween<PositionTarget> | null {
  const { target, fromPos, toPos, duration, group, onComplete } = config
  if (duration <= 0) {
    target.x = toPos.x
    target.y = toPos.y
    onComplete?.()
    return null
  }
  target.x = fromPos.x
  target.y = fromPos.y
  const curve = config.easing ?? SNAP_OUT_VALUE
  const easing = cubicBezierEasing(curve[0], curve[1], curve[2], curve[3])
  const tween = new Tween(target, group)
    .to({ x: toPos.x, y: toPos.y }, duration)
    .easing(easing)
    .onComplete(() => onComplete?.())
    .start()
  return tween
}
